import { IconDefinition, faEye, faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AppLink from "components/common/AppLink";
import Button from "components/common/button/Button";
import DeleteConfirmationModal from "components/common/confirmation/DeleteConfirmationModal";
import { DatalistRowActionType, DatalistRowActionTypeLink } from "types/datalist-types";
import useAppContext from "utilities/hooks/useAppContext";

export type DatalistRowActionsPropsType<T> = DatalistRowActionType<T> & {
    item: T,
    itemId: number | string,
    view: 'table' | 'cart'
    name: string,
    onDelete: () => void
}

type DatalistRowActionProps<T> = DatalistRowActionsPropsType<T> & {
    action?: DatalistRowActionTypeLink<T>,
    icon: IconDefinition,
    keyName: string
    title: string,
    name?: string,
}

const DatalistRowAction = <T,>(props: DatalistRowActionProps<T>) => {
    const context = useAppContext();

    if (!props.action) return <></>;
    if (typeof props.action === 'function') {
        return (
            <Button name={props.name} title={props.title} theme="link primary" onClick={e => (props.action as any)(props.item, e)} key={props.keyName}>
                <FontAwesomeIcon icon={props.icon} />
            </Button>
        );
    } else {
        const viewUrl = typeof props.action === 'string' ? props.action : props.action!.url;
        const type = typeof props.action === 'string' ? 'link' : props.action!.type;
        switch (type) {
            case 'modal':
                const paramsModal: any = typeof props.action === 'object' && props.action.params ? props.action.params(props.item) : props.itemId;
                return (
                    <Button name={props.name} title={props.title} theme="link primary" onClick={() => context.modal.open(viewUrl!, paramsModal)} key={props.keyName}>
                        <FontAwesomeIcon icon={props.icon} />
                    </Button>
                );
            default:
                const paramsLink: any = typeof props.action === 'object' && props.action.params ? props.action.params(props.item) : { id: props.itemId };
                return (
                    <AppLink name={props.name} title={props.title} className="text-primary my-auto" to={viewUrl} params={paramsLink} key={props.keyName}>
                        <FontAwesomeIcon icon={props.icon} />
                    </AppLink>
                );
        }
    }
}

const DatalistRowActions = <T,>(props: DatalistRowActionsPropsType<T>) => {
    const context = useAppContext();
    let hasPermission = true;
    const key = `${props.name}-${props.itemId}`;
    const deleteHashName = `${key}-datalist-delete`;
    const actions: React.ReactNode[] = [];
    if (props.viewUrl) {
        actions.push(<DatalistRowAction
            {...props}
            icon={faEye}
            keyName={`${key}-view`} 
            key={`${key}-view`} 
            action={props.viewUrl}
            title={context.t('button.view')}
        />)
    }

    props.buttons?.forEach((b) => {
        actions.push(
            <Button key={`${key}-button-${b.name}`} title={b.title} onClick={(e) => b.onClick(props.item, e)} theme={b.theme || 'link primary'}>
                <FontAwesomeIcon icon={b.icon} />
            </Button>
        );
    })

    if (props.mutationPermissionRole !== undefined) {
        if (typeof props.mutationPermissionRole === 'boolean') hasPermission = props.mutationPermissionRole;
        else if (typeof props.mutationPermissionRole === 'string') hasPermission = context.userHasRole(props.mutationPermissionRole);
        else if (Array.isArray(props.mutationPermissionRole)) hasPermission = context.userContainsRoles(props.mutationPermissionRole);

        if (hasPermission) {
            if (props.editUrl) {
                actions.push(<DatalistRowAction
                    {...props}
                    key={`${key}-edit`}
                    keyName={`${key}-edit`}
                    action={props.editUrl}
                    icon={faPen}
                    title={context.t('button.edit')}
                />)
            }
            if (props.delete) {
                actions.push(
                    <Button className="text-danger" title={context.t('button.delete')} theme="link" onClick={() => context.modal.open(deleteHashName, props.itemId)} key={`${key}-delete`}>
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                );
            }
        }
    }
    const containerClassName = props.view === 'table' ? 'flex gap-2' : ('w-full grid grid-cols-' + actions.length);

    const onDelete = async (e: React.MouseEvent) => {
        const id = Number(context.searchParams.get(deleteHashName));
        if (id) {
            try {
                await props.delete?.(props.item, e);
                context.notify('success', 'request.delete.success');
                props.onDelete();
            } catch (error) {
                context.notify('error', 'request.delete.error');
                throw error;
            }
        }
    };

    return (
        <>
            <div className={containerClassName}>
                {actions}
            </div>
            <DeleteConfirmationModal title="request.delete.confirmation" onConfirm={onDelete} onReject={true} name={deleteHashName} text="" />
        </>
    );
};

export default DatalistRowActions;
